import * as styles from "./OpportunitiesList.module.css";

import { ButtonElement, Input, Select } from "@components/ui";
import React, { useCallback, useEffect, useState } from "react";

import { IOpportunity } from "@models/hooks";
import { OpportunitiesListItem } from "@components/page-specific/careers";
import { OpportunitiesListMethods } from "@classes";
import { Pagination } from "@components/page-layouts";
import { navigate } from "gatsby";
import { opportunitiesContent } from "@constants";

/**
 * OpportunitiesList component displays a list of career opportunities.
 * It allows users to search for specific opportunities based on category and location.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Array} props.opportunities - The list of career opportunities.
 * @param {string} [props.type=""] - The type of career opportunities.
 * @param {number} [props.page=1] - The current page number.
 * @param {string} [props.queryCategory=""] - The category selected in the search.
 * @param {string} [props.queryLocation=""] - The location selected in the search.
 * @returns {JSX.Element} - The rendered OpportunitiesList component.
 */
const OpportunitiesList = ({
  opportunities,
  type = "",
  page = 1,
  queryCategory = "",
  queryLocation = "",
}: {
  opportunities: { node: IOpportunity }[];
  type?: "head-office" | "engineer" | "partner" | "";
  page?: number;
  queryCategory?: string;
  queryLocation?: string;
}) => {
  // State variables
  const [category, setCategory] = useState(queryCategory); // Selected category in the search
  const [location, setLocation] = useState(queryLocation); // Selected location in the search
  const [prevLoaded, setPrevLoaded] = useState(false); // Flag to track if the page has been loaded previously

  // Create an instance of OpportunitiesListMethods
  const opportunitiesListMethods = new OpportunitiesListMethods(
    opportunities,
    type
  );
  const defaultCategory =
    queryCategory.length > 0
      ? opportunitiesListMethods.categoryOptions.find(
          (x) => x.value === category
        )
      : undefined; // Default selected category
  const [searchError, setSearchError] = useState(
    opportunitiesListMethods.defaultError
  ); // Error message to display if no opportunities match the search criteria
  const [filteredOpportunities, setFilteredOpportunities] = useState(
    opportunities.slice(
      page * opportunitiesListMethods.postCount -
        opportunitiesListMethods.postCount,
      page * opportunitiesListMethods.postCount
    )
  ); // Filtered list of opportunities based on search criteria
  const [maxPages, setMaxPages] = useState(
    opportunitiesListMethods.getDefaultMaxPageNumber()
  ); // Maximum number of pages for pagination

  /**
   * Handles the search button click event.
   * Navigates to the search results page with the selected category and location.
   */
  const onSearchHandler = useCallback(() => {
    navigate(
      `/careers/opportunities${
        type ? `/${type}` : ""
      }?category=${category}&location=${location}`
    );
  }, [category, location, type]);

  // Fetch and update the filtered opportunities based on the selected category and location
  useEffect(() => {
    const getOpportunitiesForPage = async () => {
      setSearchError(opportunitiesListMethods.defaultError);
      try {
        const newOpportunities = await opportunitiesListMethods.getPostsForPage(
          page,
          queryCategory,
          queryLocation
        );
        const newMaxPages = opportunitiesListMethods.getMaxPageNumber();
        setFilteredOpportunities(newOpportunities);
        setMaxPages(newMaxPages);
        if (
          newOpportunities.length === 0 &&
          (queryCategory.length > 0 || queryLocation.length > 0)
        )
          setSearchError(
            "We're sorry, there are currently no opportunities that match your search. Please check your search criteria and try again."
          );
      } catch (error: any) {
        setSearchError(error.message);
      }
    };
    if (
      prevLoaded ||
      (!prevLoaded && (queryCategory.length > 0 || queryLocation.length > 0))
    )
      getOpportunitiesForPage();
    if (!prevLoaded) setPrevLoaded(true);
  }, [page, queryCategory, queryLocation]);

  return (
    <section
      id="opportunitiesListContainer"
      className={`inner ${styles.opportunitiesListContainer}`}
    >
      {/* Search input section */}
      <h2>Use the fields below to search for your ideal opportunity</h2>
      <div className={styles.opportunitiesListSearch}>
        <strong>I am looking for a </strong>
        {/* Category select input */}
        <Select
          name="selOpportunityCategory"
          placeholder="Select your ideal role"
          title="Category"
          options={opportunitiesListMethods.categoryOptions}
          required={false}
          className={styles.opportunitiesListSearchInput}
          setValue={(value) => {
            setCategory(value);
          }}
          defaultSelectedOption={defaultCategory}
        />
        {type === "head-office" ? (
          <strong> vacancy in our head office</strong>
        ) : (
          <>
            <strong> vacancy in </strong>
            {/* Location input */}
            <Input
              name="txtOpportunityLocation"
              placeholder={
                queryLocation.trim().length > 0
                  ? queryLocation
                  : "Choose your location"
              }
              title="Location"
              type="text"
              required={false}
              className={styles.opportunitiesListSearchInput}
              setValue={(value) => {
                setLocation(value);
              }}
              defaultValue={
                queryLocation.trim().length > 0 ? queryLocation : ""
              }
            />
          </>
        )}
        {/* Search button */}
        <ButtonElement
          onClick={onSearchHandler}
          title="Search"
          showLoading={true}
        />
      </div>

      {/* Error message section */}
      {searchError.length > 0 && (
        <div className={styles.opportunitiesListSearchError}>
          <p>{searchError}</p>
        </div>
      )}

      {/* List of filtered opportunities */}
      <div className={styles.opportunitiesList}>
        {filteredOpportunities.map(({ node: opp }) => {
          return (
            <OpportunitiesListItem
              key={`opportunitiesListItem_${opp.id}`}
              opp={opp}
              opportunitiesContent={opportunitiesContent}
            />
          );
        })}
      </div>

      {/* Pagination */}
      <Pagination
        maxPages={maxPages}
        curPage={page}
        pageType={`careers/opportunities${type.length > 0 ? `/${type}` : ""}`}
      />
    </section>
  );
};

export default OpportunitiesList;
